import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Calendar, Users, Clock, MapPin, ArrowRight, Sun, Flame } from 'lucide-react';

interface TodayData {
  hebrewDate: {
    day: number;
    month: number;
    monthName: string;
    monthHebrew: string;
    year: number;
    hebrewFormat: string;
  };
  holidays: string[];
  parsha: string | null;
  candleLighting: string | null;
  havdalah: string | null;
  zmanim: {
    sunrise: string;
    latestShema: string;
    latestShacharit: string;
    midday: string;
    earliestMincha: string;
    plagHaMincha: string;
    sunset: string;
    nightfall: string;
  };
}

interface Organization {
  id: string;
  name: string;
  logoUrl: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  website: string | null;
}

interface Event {
  id: string;
  title: string;
  description: string | null;
  type: string;
  location: string | null;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
}

interface HomePageProps {
  org: Organization | undefined;
  orgSlug: string;
  onNavigate: (page: 'home' | 'events' | 'about' | 'contact') => void;
}

export function HomePage({ org, orgSlug, onNavigate }: HomePageProps) {
  const { data: events } = useQuery({
    queryKey: ['upcoming-events', orgSlug],
    queryFn: async () => {
      const now = new Date().toISOString();
      const response = await api.get<Event[]>(`/events?from=${now}&limit=3`);
      if (!response.success) return [];
      return response.data || [];
    },
  });

  const { data: todayData } = useQuery({
    queryKey: ['today-calendar'],
    queryFn: async () => {
      const response = await api.get<TodayData>('/calendar/today');
      if (!response.success) return null;
      return response.data;
    },
  });

  const formatEventDate = (dateStr: string, allDay: boolean) => {
    const date = new Date(dateStr);
    if (allDay) {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Welcome to {org?.name || 'Our Synagogue'}
            </h1>
            <p className="mt-6 text-xl text-primary-100">
              A warm and welcoming community dedicated to Jewish life, learning, and tradition.
              Join us for services, classes, and celebrations.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => onNavigate('events')}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-700 shadow-sm hover:bg-primary-50 transition-colors"
              >
                <Calendar className="h-5 w-5" />
                View Events
              </button>
              <button
                onClick={() => onNavigate('contact')}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Jewish Calendar Section */}
      {todayData && (
        <section className="py-12 bg-amber-50 border-b border-amber-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-3">
              {/* Hebrew Date */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-200 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <Calendar className="h-6 w-6 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{todayData.hebrewDate.hebrewFormat}</p>
                <p className="text-gray-600 mt-1">
                  {todayData.hebrewDate.day} {todayData.hebrewDate.monthName} {todayData.hebrewDate.year}
                </p>
                {todayData.parsha && (
                  <p className="text-primary-600 font-medium mt-2">Parashat {todayData.parsha}</p>
                )}
                {todayData.holidays.length > 0 && (
                  <p className="text-amber-600 text-sm mt-2">{todayData.holidays.join(' • ')}</p>
                )}
              </div>

              {/* Candle Lighting / Shabbat Times */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-200 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <Flame className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Shabbat Times</h3>
                {todayData.candleLighting ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Candle Lighting</span>
                      <span className="font-semibold text-amber-600">{todayData.candleLighting}</span>
                    </div>
                    {todayData.havdalah && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Havdalah</span>
                        <span className="font-semibold text-gray-900">{todayData.havdalah}</span>
                      </div>
                    )}
                  </div>
                ) : todayData.havdalah ? (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Havdalah</span>
                    <span className="font-semibold text-gray-900">{todayData.havdalah}</span>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Candle lighting times appear on Friday
                  </p>
                )}
              </div>

              {/* Zmanim */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-200">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Sun className="h-5 w-5 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Today's Zmanim</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <ZmanimRow label="Sunrise" time={todayData.zmanim.sunrise} />
                  <ZmanimRow label="Latest Shema" time={todayData.zmanim.latestShema} highlight />
                  <ZmanimRow label="Sunset" time={todayData.zmanim.sunset} highlight />
                  <ZmanimRow label="Nightfall" time={todayData.zmanim.nightfall} />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Service Times */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Service Times</h2>
            <p className="mt-2 text-gray-600">Join us for prayer and community</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Shabbat Services</h3>
              <p className="text-gray-600 mt-2">Friday 6:00 PM</p>
              <p className="text-gray-600">Saturday 9:30 AM</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Weekday Minyan</h3>
              <p className="text-gray-600 mt-2">Morning: 7:30 AM</p>
              <p className="text-gray-600">Evening: 6:00 PM</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Sunday Services</h3>
              <p className="text-gray-600 mt-2">Morning: 9:00 AM</p>
              <p className="text-gray-600">Religious School: 10:00 AM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {events && events.length > 0 && (
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Upcoming Events</h2>
                <p className="mt-2 text-gray-600">What's happening in our community</p>
              </div>
              <button
                onClick={() => onNavigate('events')}
                className="hidden sm:inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                View all events
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-primary-600 mb-2">
                      <Calendar className="h-4 w-4" />
                      {formatEventDate(event.startTime, event.allDay)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                    {event.description && (
                      <p className="text-gray-600 mt-2 line-clamp-2">{event.description}</p>
                    )}
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                        <MapPin className="h-4 w-4" />
                        {event.location}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <button
                onClick={() => onNavigate('events')}
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                View all events
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Join Our Community */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-12 w-12 mx-auto mb-4 text-primary-200" />
          <h2 className="text-3xl font-bold">Join Our Community</h2>
          <p className="mt-4 text-xl text-primary-100 max-w-2xl mx-auto">
            Become a member and be part of a vibrant, supportive Jewish community.
            We welcome families and individuals of all backgrounds.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => onNavigate('about')}
              className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-50 transition-colors"
            >
              Learn More
            </button>
            <button
              onClick={() => onNavigate('contact')}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-400 transition-colors"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ZmanimRow({ label, time, highlight }: { label: string; time: string; highlight?: boolean }) {
  return (
    <div className={`flex justify-between items-center py-1 ${highlight ? 'font-medium' : ''}`}>
      <span className={highlight ? 'text-gray-900' : 'text-gray-600'}>{label}</span>
      <span className={highlight ? 'text-amber-600' : 'text-gray-900'}>{time}</span>
    </div>
  );
}
